/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * CỘT "NỘI DUNG" CỦA BẢNG LỆNH — việc #8 (NV102 mục 1, dev_device xếp làm TRƯỚC vì lee nhìn
 * thấy nó hằng ngày).
 *
 * 🔴 LỖI LÀ LỖI HÌNH DẠNG, không phải lỗi thiếu trường:
 *
 *     sendControl()   payload: { outputs: desired }   với  desired: boolean[]
 *     renderer cũ     (p.outputs as {index,on}[]).map(o => `ngõ ${o.index}: …`)
 *
 *   Mảng `boolean[]` đọc bằng `o.index` ra `undefined`, `o.on` ra `undefined` ⇒ mỗi hàng in
 *   **`ngõ undefined: TẮT`**, ba lần. Nó không sai một chữ — nó sai một tầng.
 *
 * ⛔ VÀ ĐÂY LÀ HỌ LỖI ĐÃ CẮN CẢ TUẦN: một giá trị KHÔNG CÓ được vẽ ra thành một câu có nghĩa.
 *   `ngõ undefined: TẮT` đọc như *"có một ngõ nào đó đang tắt"*, trong khi sự thật là **ba ngõ,
 *   biết rõ ngõ nào**. Người trực đọc bảng này để dựng lại việc đã xảy ra.
 *
 * ⚠ TÁCH RA KHỎI `CabinetDetail.tsx` để ĐO ĐƯỢC. Hàm cũ nằm trong tệp trang, mà bộ kiểm không
 *   nhập được tệp `.tsx` trang (kéo theo cả React). *"Không đo được là một mùi thiết kế"* —
 *   cùng lý do đã tách `laLenhDongCat()` sang `doccsdl.ts`.
 *
 * 📜 BỐN HÌNH DẠNG, và mỗi hình dạng là MỘT đời của hợp đồng — giữ cả bốn, vì CSDL còn giữ
 *   hàng cũ và bảng này chính là chỗ đọc lại lịch sử:
 *     · `{ outputs: boolean[] }`        `CMD_SET_ALL_OUTPUTS` hôm nay (dây: `outputStates`)
 *     · `{ outputs: {index,on}[] }`     hàng đời cũ
 *     · `{ output: N, on: bool }`       `CMD_SET_OUTPUT` (kind = 1) — việc #7, CHƯA bật
 *     · `{ revision: '…' }`             gói lịch
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** Ngăn cách theo đúng mẫu dev_device chốt: `ngõ 1: BẬT · ngõ 2: TẮT · ngõ 3: TẮT`. */
const NGAN = ' · ';

const cauNgo = (viTri: number, bat: boolean) => `ngõ ${viTri}: ${bat ? 'BẬT' : 'TẮT'}`;

/**
 * ⛔ KHÔNG BAO GIỜ TRẢ RA CHỮ `undefined`. Không đọc được hình dạng thì **nói ra là không đọc
 *   được**, kèm JSON thô — im lặng hay `undefined` đều bị đọc thành một câu có nghĩa.
 */
export function moTaTaiLenh(payload: unknown): string {
  if (payload === null || payload === undefined || typeof payload !== 'object') return '—';
  const p = payload as Record<string, unknown>;

  if (Array.isArray(p.outputs)) {
    const ds = p.outputs as unknown[];
    if (ds.length === 0) return 'không ngõ nào (mảng rỗng)';
    // Đời NAY: mảng boolean thuần, THỨ TỰ là số hiệu ngõ (1,2,3) — `kapp_v2.proto:130`.
    if (ds.every((x) => typeof x === 'boolean')) {
      return (ds as boolean[]).map((bat, i) => cauNgo(i + 1, bat)).join(NGAN);
    }
    // Đời CŨ: mảng object. Chỉ nhận phần tử ĐỦ HAI trường — thiếu thì khai là thiếu.
    return ds.map((x, i) => {
      const o = x as Record<string, unknown>;
      const so = typeof o?.index === 'number' ? o.index
               : typeof o?.output === 'number' ? o.output : null;
      const bat = typeof o?.on === 'boolean' ? o.on : null;
      if (so === null || bat === null) return `phần tử ${i + 1}: KHÔNG ĐỌC ĐƯỢC`;
      return cauNgo(so, bat);
    }).join(NGAN);
  }

  // `CMD_SET_OUTPUT` (kind = 1) — việc #7. Đọc được TỪ HÔM NAY, để ngày bật nút thì bảng
  // không phải đợi thêm một lượt vá nữa. Đọc sớm KHÔNG phải gửi sớm.
  if (typeof p.output === 'number' && typeof p.on === 'boolean') return cauNgo(p.output, p.on);

  if (typeof p.revision === 'string') return `phiên bản lịch ${p.revision}`;

  const tho = JSON.stringify(p);
  return tho.length > 120 ? `${tho.slice(0, 120)}…` : tho;
}
